import { DoNotDisturb, MoreVert } from "@mui/icons-material"
import PaymentIcon from "@mui/icons-material/Payment"
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  SxProps,
  Typography,
} from "@mui/material"
import { noop } from "lodash"
import moment from "moment"
import React, { FC, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { LockerDoorReservationSession } from "shared/lockerDoorReservationSession"
import type { Sst2Door } from "shared/sst2"
import { useExpirationWarningMinutes } from "src/hooks/useExpirationWarningMinutes"
import { isReservationSessionActive } from "../reservationSession"
import { computeSst2DoorState } from "../sst2DoorState"
import { isSst2ReservationVisible } from "../sst2Status"
import FlashingDots from "./FlashingDots"

const menuOptions = ["removeReservationSession"]

type OsLockerDoorV2Props = {
  door: Sst2Door
  isSelected?: boolean
  onClick?: () => void
  cellSize?: string | null
  /** Door has an active SST reservation with no matching SL2 reservation */
  isUnlinked?: boolean
  /** A reservation create request for this door is in flight — show a spinner
   *  in place of any other state until the response settles. */
  isCreatingReservation?: boolean
  /**
   * Render EVERY state as clickable — including the ones the operator dashboard
   * deliberately makes inert (unlinked, disabled, DRAFT, create-in-flight).
   *
   * Why the flag rather than always-on: on the operator dashboard a click ACTS
   * on the door, so those tiles must stay dead. On the SA console a click only
   * INSPECTS it, and an unlinked or draft-stuck tile is the one an admin needs
   * most — it is the desync the inspector exists to explain.
   */
  alwaysClickable?: boolean
  /** Another operator (or this one) holds a soft lock on this door while
   *  filling the reservation form — DEV-201. Renders the same "reserving"
   *  tile V1 shows and blocks the click. */
  session?: LockerDoorReservationSession
  onRemoveReservationSession?: (sessionId: string) => void
}

export const OsLockerDoorV2: FC<OsLockerDoorV2Props> = React.memo(
  ({
    door,
    isSelected = false,
    onClick = noop,
    cellSize,
    isUnlinked = false,
    isCreatingReservation = false,
    alwaysClickable = false,
    session,
    onRemoveReservationSession,
  }) => {
    const { t } = useTranslation()
    const isSmall = cellSize?.toLowerCase() === "small"
    const doorLabel = door.door_label

    // Tick every second to update expiration countdown (matches V1 useReservation behavior)
    const [now, setNow] = useState(new Date())

    const activeReservations = useMemo(
      () => (door.reservations || []).filter(isSst2ReservationVisible),
      [door.reservations],
    )
    const firstReservation = activeReservations[0]
    const hasActiveReservation = activeReservations.length > 0
    // Mirror V1's hasPayment contract (useReservation): show a payment chip
    // on the door tile when the active reservation has a positive amount.
    // V2 carries `amount` directly on the embedded Sst2Reservation.
    const hasPayment = (firstReservation?.amount ?? 0) > 0

    // Only tick when there's a reservation with an expiration to count down,
    // or a soft lock whose sessionEnd we may need to cross.
    const needsTicking =
      !!session ||
      (hasActiveReservation &&
        firstReservation?.expiration &&
        firstReservation.status !== "DRAFT")

    useEffect(() => {
      if (!needsTicking) return
      const id = setInterval(() => setNow(new Date()), 1000)
      return () => clearInterval(id)
    }, [needsTicking])

    // "About to expire" window is tenant-configurable — V1 reads the same
    // config in useReservation. Hard-coding it here meant the orange warning
    // only appeared in the last 15 min instead of the configured window.
    const warningMinutes = useExpirationWarningMinutes()

    // Compute visual state matching V1's useReservation logic
    const {
      statusColor: reservationStatusColor,
      statusTextColor: reservationStatusTextColor,
      hasExpired,
      showExpirationWarning,
      isDelivered,
      isDraft,
      expiresIn,
    } = useMemo(
      () =>
        computeSst2DoorState({
          reservation: hasActiveReservation ? firstReservation : null,
          now,
          warningMinutes,
        }),
      [hasActiveReservation, firstReservation, now, warningMinutes],
    )

    // DEV-201 — soft lock held by an operator filling the reservation form.
    // An actual reservation always wins over a session, so a stale session
    // can never mask a real parcel (same rule as V1's useReservation).
    const isReserving = useMemo(
      () =>
        !hasActiveReservation &&
        isReservationSessionActive(session, now.getTime()),
      [session, hasActiveReservation, now],
    )

    // V1 paints a held door light-blue with primary-coloured text. Override
    // here rather than threading `session` into computeSst2DoorState — that
    // stays a pure function of the SST reservation, and its unit tests with it.
    const statusColor = isReserving ? "lightblue" : reservationStatusColor
    const statusTextColor = isReserving
      ? "primary.main"
      : reservationStatusTextColor

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const menuOpen = Boolean(anchorEl)
    // Unique per tile — several doors can hold a session at once.
    const menuId = `door-session-menu-${door.id}`
    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
      // The menu button sits on top of the tile — don't also fire onClick.
      event.stopPropagation()
      setAnchorEl(event.currentTarget)
    }
    const handleMenuClose = () => setAnchorEl(null)
    const handleOptionClick = (option: string) => {
      if (option === "removeReservationSession" && session?.id) {
        onRemoveReservationSession?.(session.id)
      }
      setAnchorEl(null)
    }

    // A door held by a session behaves like a DRAFT one: not clickable.
    const isLocked = isDraft || isReserving

    const wrapperSx: SxProps = {
      bgcolor: statusColor,
      color: statusTextColor,
      width: "100%",
      height: "100%",
      position: "relative",
      boxShadow: isSelected ? "3px 3px 8px 1px rgba(0,0,0,0.8)" : "none",
      "&:hover":
        isLocked && !alwaysClickable
          ? { cursor: "not-allowed" }
          : { cursor: "pointer" },
    }

    /**
     * Props that turn one of the inert tiles below into a real control.
     *
     * A tile that becomes mouse-clickable must become keyboard-reachable in the
     * same change, otherwise the inspector is only openable with a pointer.
     */
    const inspectProps = alwaysClickable
      ? {
          onClick,
          role: "button" as const,
          tabIndex: 0,
          "aria-label": `${t("saLockerConsole.door")} ${doorLabel}`,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onClick()
            }
          },
        }
      : {}

    // In-flight reservation create — show a spinner in place of any other
    // state until the response settles. Takes precedence over isUnlinked /
    // is_enable so the tile doesn't briefly flash gray while SST has
    // already created the V2 reservation but SL2 hasn't returned yet.
    if (isCreatingReservation) {
      return (
        <Paper
          variant="outlined"
          {...inspectProps}
          sx={{
            borderRadius: 4,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: alwaysClickable ? "pointer" : undefined,
          }}
        >
          <CircularProgress size={isSmall ? 20 : 28} />
        </Paper>
      )
    }

    // Disabled state — the door is switched off (is_enable), or SST says it
    // cannot take a reservation (is_available) and none is showing on it.
    // Without the is_available half, an empty door SST refuses still rendered
    // as available and the create call failed on the SST side (LOCKER-492).
    // A visible reservation keeps its tile whatever is_available says.
    const isUnavailable =
      (door as any).is_enable === false ||
      (!hasActiveReservation && door.is_available === false)
    if (isUnavailable) {
      return (
        <Paper
          variant="outlined"
          elevation={isSelected ? 20 : 0}
          {...inspectProps}
          sx={{
            bgcolor: "lightgrey",
            borderRadius: 4,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: alwaysClickable ? "pointer" : undefined,
          }}
        >
          <Stack direction="column" spacing={1} alignItems="center">
            <DoNotDisturb sx={{ fontSize: 40 }} />
            <Typography variant="body2">{t("unavailable")}</Typography>
          </Stack>
        </Paper>
      )
    }

    // Unlinked state — SST has a reservation but SL2 doesn't know about it
    if (isUnlinked) {
      return (
        <Paper
          variant="outlined"
          {...inspectProps}
          sx={{
            bgcolor: "#e0e0e0",
            borderRadius: 4,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            cursor: alwaysClickable ? "pointer" : undefined,
          }}
        >
          <Stack
            direction="column"
            spacing={0}
            alignItems="center"
            sx={{ p: 1 }}
          >
            <Box
              sx={{
                borderRadius: "50%",
                width: 23,
                height: 23,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid grey",
                mb: 1,
              }}
            >
              <Typography variant="caption" color="grey">
                {doorLabel}
              </Typography>
            </Box>
            <DoNotDisturb sx={{ fontSize: 28, color: "grey" }} />
            <Typography variant="caption" color="grey" textAlign="center">
              {t("unavailable")}
            </Typography>
          </Stack>
        </Paper>
      )
    }

    return (
      <Paper
        variant={isSelected ? "elevation" : "outlined"}
        elevation={isSelected ? 20 : 0}
        className={`uf-door ${
          !hasActiveReservation && !isLocked
            ? "uf-door-available"
            : "uf-door-unavailable"
        } ${hasExpired ? "uf-door-expired" : ""} ${showExpirationWarning && !hasExpired ? "uf-door-expiring-soon" : ""}`}
        sx={{ ...wrapperSx, borderRadius: 4 }}
        onClick={isLocked && !alwaysClickable ? noop : onClick}
      >
        <Stack direction="column" spacing={0} sx={{ height: "100%" }}>
          {/* Door number circle — matches V1: 23x23px circle with border */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 1 }}
          >
            <Box
              sx={{
                borderRadius: "50%",
                width: 23,
                height: 23,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: statusTextColor,
              }}
            >
              <Typography variant="caption">{doorLabel}</Typography>
            </Box>
            {hasPayment && !isLocked && (
              <PaymentIcon
                sx={{
                  fontSize: isSmall ? "1rem" : "1.25rem",
                  color: statusTextColor,
                }}
              />
            )}
          </Stack>

          {/* DRAFT (SST-side) or RESERVING (SL2 soft lock) — reservation in
              progress with flashing dots. A session additionally shows when
              it started and offers the operator an escape hatch to clear it. */}
          {isLocked && (
            <Stack
              direction="column"
              spacing={isSmall ? 0 : 1}
              justifyContent="center"
              height="100%"
              sx={{ px: 1 }}
            >
              <Typography
                fontSize="0.75rem"
                variant="body1"
                fontWeight="bold"
                sx={{ color: "primary.main" }}
              >
                {t("reservation")}
              </Typography>
              <FlashingDots />
              {isReserving && session?.sessionStart && (
                <Typography variant="caption" color="primary.main">
                  {`${t("since")} ${moment(session.sessionStart).format(
                    "HH[h]mm",
                  )}`}
                </Typography>
              )}
            </Stack>
          )}

          {isReserving && (
            <>
              <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                <IconButton
                  aria-label={t("removeReservationSession")}
                  aria-controls={menuOpen ? menuId : undefined}
                  aria-expanded={menuOpen ? "true" : undefined}
                  aria-haspopup="true"
                  onClick={handleMenuClick}
                >
                  <MoreVert sx={{ color: "grey" }} />
                </IconButton>
              </Box>
              <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
              >
                {menuOptions.map((option) => (
                  <MenuItem
                    key={option}
                    onClick={() => handleOptionClick(option)}
                  >
                    {t(option)}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* AVAILABLE state — no active reservation */}
          {!hasActiveReservation && !isLocked && (
            <Stack
              direction="column"
              pl={1}
              spacing={0}
              justifyContent="center"
              height="calc(100% - 30px)"
            >
              <Typography variant="body2" fontWeight="bold" color="whitesmoke">
                {t("available")}
              </Typography>
              <Box
                sx={{
                  height: 0,
                  width: "80%",
                  maxWidth: 130,
                  borderBottom: "2px solid rgba(255,255,255,0.5)",
                }}
              />
            </Stack>
          )}

          {/* OCCUPIED state — active reservation (CONFIRMED, DELIVERED, EXPIRED) */}
          {hasActiveReservation && !isDraft && (
            <Stack
              direction="column"
              spacing={0}
              justifyContent="center"
              height="calc(100% - 30px)"
              sx={{ px: 1, color: statusTextColor }}
            >
              {/* Status label */}
              <Typography
                variant={isSmall ? "caption" : "body2"}
                fontWeight="bold"
                sx={isSmall ? { fontSize: "0.8rem" } : undefined}
              >
                {hasExpired
                  ? t("expired")
                  : !isDelivered
                    ? t("waitingDeposit")
                    : t(isSmall ? "waitingPickupShort" : "waitingPickup")}
              </Typography>

              {/* Separator line */}
              <Box
                sx={{
                  height: 0,
                  width: "80%",
                  maxWidth: 160,
                  borderBottom: "2px solid",
                  borderColor: statusTextColor,
                }}
              />

              {/* Expiration countdown */}
              {expiresIn && (
                <>
                  {!isSmall && (
                    <Typography variant="caption">
                      {hasExpired ? t("since") : t("expire")}
                    </Typography>
                  )}
                  <Typography variant={isSmall ? "caption" : "h6"}>
                    {expiresIn}
                  </Typography>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>
    )
  },
)

OsLockerDoorV2.displayName = "OsLockerDoorV2"
